import { Vue, Component } from "vue-property-decorator";
import { Action, Getter, Mutation, State } from "vuex-class";
import { ROUTER, WEBAPP_LAYOUT } from "../../common/constants";
import { KANBAN_EVENTS } from "../../common/kanban.constant";
import { PropertyServices } from "../../services";
import { namespace } from "vuex-class";
import { storeModules } from "../../store";
import { Locale } from "../../models/locale-model";
import propertyEditTypesStore from "../../store/property/edit/property-edit-types.store";
import { IPropertyModel } from "../../models/property";
import { PropertyEditModule } from "../../store/property/edit";
import moduleConstant from "../../common/module-constant";
import { generateNewTabId } from "../../utils/cached-data";
import KanbanStoreTypes  from "../../store/kanban/kanban-types.stores";
import { EXTERNAL_APP_URI, KANBAN_ORIGIN } from "../../common/uri/property-uri.constants";
import { LanguageHelper } from "../../utils/language-helper";
import { embeddedAppTokenBroker } from "../../utils/embedded-app-token";
import { DrawerModel, IDrawerModel } from "../../models/common/drawer.model";
import { DRAWER_NAME } from "../../common/drawer-component.constants";
import { postMessageForWebview } from "../../utils/web-view";
import { resolveKanbanWebviewAction } from "./kanban-webview-bridge";
import type { KanbanWebviewAction } from "./kanban-webview-bridge";
// import { PropertyModel } from "../../models/property";

const CommonAction = namespace(storeModules.Common, Action);
const CommonState = namespace(storeModules.Common, State);
const CommonMutation = namespace(storeModules.Common, Mutation);
const PropertyAction = namespace(storeModules.PropertyEdit, Action);
const PropertyGetter = namespace(storeModules.PropertyEdit, Getter);
const KanbanMutation = namespace(storeModules.Kanban, Mutation);
const KanbanGetter = namespace(storeModules.Kanban, Getter);

@Component({
	name: ROUTER.KANBAN,
	template: require("./template.html"),
	components: {
		'relation': () => import('../property/components/relation/RelationComponent.vue')
	}
})
export default class KanbanWebsite extends Vue {
	@CommonAction('setAgendaPrefetchProperty') private storeSetAgendaPrefetchProperty: (payload: { Id: number, Info: Object }) => Promise<any>;
	@CommonState('webappLayoutMode') webappLayoutMode: string;
	@CommonMutation setVisibleDrawerModel: (payload: IDrawerModel) => void;
    @PropertyAction(propertyEditTypesStore.Get.PROPERTY)
    storeGetPropertyById: (payload: {
        locales: Locale[];
        langCode: string;
        propertyId: number;
        loadTabs: string[];
    }) => Promise<IPropertyModel>;
	@PropertyGetter property: IPropertyModel;

	@KanbanMutation(KanbanStoreTypes.Set.KANBAN_URL)
	storeGetKanbanUrl: (payload: string ) => Promise<string>;

	@KanbanGetter kanbanUrl: string;

	isLoading = true;
	propertyService: PropertyServices = new PropertyServices();
	kanbanWebsiteUrl = ''
	private unregisterAccessToken: () => void = null;

	// This is for create new relation in property detail view
	// propertyDetail: any = null;

	async created() {
		this.$registerModule(storeModules.PropertyEdit, new PropertyEditModule())
	}

	mounted() {
		this.isLoading = true;
        this.handleMessage = this.handleMessage.bind(this);
		window.addEventListener("message", this.handleMessage);
		this.unregisterAccessToken = embeddedAppTokenBroker.register({
			origin: KANBAN_ORIGIN,
			getContentWindow: () => (this.$refs.kanbanIframe as HTMLIFrameElement)?.contentWindow || null,
			getLanguageId: () => LanguageHelper.convertLanguageInt(this.$i18n.locale)
		});

		this.loadKanbanApp();
	}

	// Rebuild from the base URL, but keep where the user is inside the Kanban app
	// (the URL_CHANGE handler stores that location in kanbanUrl).
	private loadKanbanApp(): void {
		const location = this.currentAppLocation();
		const language = LanguageHelper.convertToWebviewLang(LanguageHelper.convertLanguageInt(this.$i18n.locale));
		const appQuery = location.query ? `&${location.query}` : "";
		this.storeGetKanbanUrl(`${EXTERNAL_APP_URI.KANBAN_APP}${location.path}?language=${language}${appQuery}`);
		this.kanbanWebsiteUrl = this.kanbanUrl;
	}

	// Path and own query parameters of the cached Kanban URL, so a remount reloads
	// the view the user was on instead of the board root. Both empty for a
	// host-built URL, whose base carries no path.
	private currentAppLocation(): { path: string, query: string } {
		if (!this.kanbanUrl) return { path: "", query: "" };
		try {
			const current = new URL(this.kanbanUrl);
			const appParams = new URLSearchParams(current.search);
			// Re-added by the caller with the current locale.
			appParams.delete("language");
			return {
				path: current.pathname === "/" ? "" : current.pathname,
				query: appParams.toString()
			};
		} catch (error) {
			return { path: "", query: "" };
		}
	}

	get coPropertyDetail() {
		return this.property;
	}

	set coPropertyDetail(value: any) {
		this.property = value || null;
	}

	get isWebView(): boolean {
		return this.webappLayoutMode === WEBAPP_LAYOUT.WEBVIEW;
	}

	get allowLocales() {
        return Vue.config["locales"].filter(l => {
            return l.id != 4;
        });
    }

	// Event handler when the iframe finishes loading
	onIframeLoad() {
		this.isLoading = false;
	}

	async handleMessage(event: MessageEvent) {
		// Only the embedded Kanban iframe may drive navigation, drawers and WinApp calls.
		const iframe = this.$refs.kanbanIframe as HTMLIFrameElement | undefined;
		if (event.origin !== KANBAN_ORIGIN || !iframe?.contentWindow || event.source !== iframe.contentWindow) return;
		const { data } = event;
		const TYPE = data?.TYPE;
		const { type } = data

		const MessageType = TYPE || type

		// WinApp webview: redirect events must not navigate the chromeless router.
		if (this.isWebView) {
			const action = resolveKanbanWebviewAction(data, this.coPropertyDetail);
			if (action) {
				this.runWebviewAction(action);
				return;
			}
		}

		switch (MessageType) {
			case KANBAN_EVENTS.URL_CHANGE: {
				const {VALUE} = data;
				const currentUrlObj = new URL(this.kanbanUrl)
				const currentUrl = currentUrlObj?.pathname + currentUrlObj?.search
				if (currentUrl === VALUE) {
					return;
				} else {
					const newUrl = `${currentUrlObj?.origin+VALUE}`;
					await this.storeGetKanbanUrl(newUrl);
				}


				// Load the property detail
				if (VALUE.includes('lead-details')) {
					const id = VALUE.split('id=')
					await this.storeGetPropertyById({
						locales: this.allowLocales,
						langCode: this.$i18n.locale,
						propertyId: parseInt(id[1]),
						loadTabs: ["*"]
					})
				} else {
					this.coPropertyDetail = null;
				}
				break;
			}
			case KANBAN_EVENTS.VIEW_PERSON: {
				const { ID } = data;
				if (ID) {
					this.$router.push({
						name: ROUTER.CLIENT.CLIENT_VIEW,
						params: { id: ID.toString() },
					});
				}
				break;
			}

			case KANBAN_EVENTS.EDIT_PERSON: {
				const { ID } = data;
				if (ID) {
					this.$router.push({
						name: ROUTER.CLIENT.CLIENT_EDIT,
						params: { id: ID.toString() },
					});
				}
				break;
			}

			case KANBAN_EVENTS.NEW_RELATION: {
				(this as any).$refs.propertyRelations.newRelation();
				break;
			}

			case KANBAN_EVENTS.CREATE_APPOINTMENT: {
				const { ID } = data;
				if (ID) {
					console.log('this is coPropertyDetail', {
						Id: this.coPropertyDetail.Id,
						Info: {
							VisitingHours: this.coPropertyDetail?.Visitinghours,
							Reference: this.coPropertyDetail?.Reference
						}
					})
					this.storeSetAgendaPrefetchProperty({
						Id: this.coPropertyDetail.Id,
						Info: {
							VisitingHours: this.coPropertyDetail?.Visitinghours,
							Reference: this.coPropertyDetail?.Reference
						}
					}).then(() => {
						this.$router.push({
							name: ROUTER.AGENDA.AGENDA,
						})
					})
				}
				break;
			}

			case KANBAN_EVENTS.PROPERTY_EDIT: {
				const { ID } = data;
				if (ID) {
					this.$router.push({
						name: ROUTER.PROPERTY.PROPERTY_EDIT,
						params: { id: ID.toString() },
					});
				}
				break;
			}

			case KANBAN_EVENTS.CREATE_TASK: {
				const { data: { propertyId, personId, title, personReference } } = data;
				if (propertyId) {
					if (personId) {

						this.$router.push({
							name: ROUTER.TASK.TASK_NEW,
							params: { id: generateNewTabId(moduleConstant.Task) },
							query: {
								personId: personId.toString(),
								personRef: personReference.toString() || '',
								propertyId: propertyId.toString(),
								propertyRef: this.coPropertyDetail.Reference || '',
								subject: title || ''
							}
						});
					}

				}
				break;
			}

			default: break;
		}
	}

	private runWebviewAction(action: KanbanWebviewAction) {
		switch (action.kind) {
			case "winapp":
				postMessageForWebview(action.method, action.args, 'KanbanWebview');
				break;
			case "person-detail":
				this.setVisibleDrawerModel(new DrawerModel({
					componentName: DRAWER_NAME.PERSON_DETAIL,
					props: { objectId: action.personId },
					title: 'PERSON'
				}));
				break;
			case "task-drawer":
				this.setVisibleDrawerModel(new DrawerModel({
					componentName: DRAWER_NAME.TASK_NEW_FORM,
					props: { prefill: action.prefill },
					context: `kanban-webview-new-task-${Date.now()}`,
					title: 'TASK',
					showSaveBtn: true
				}));
				break;
			case "drop":
				break;
		}
	}

	refreshIframe() {
		// const iframe = (document.getElementById('kanbanIframe') as HTMLIFrameElement)
		// iframe.contentWindow?.location.reload()
	}

	beforeDestroy() {
		window.removeEventListener("message", this.handleMessage);
		this.unregisterAccessToken?.();
	}
}
